import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SelectionProcedure = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('selection_procedures');
  const [viewItem, setViewItem] = useState(null);
  const [selectionProcedures, setSelectionProcedures] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProcedure, setEditingProcedure] = useState(null);

  useEffect(() => {
    loadSelectionProcedures();
  }, []);

  const loadSelectionProcedures = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/selection-procedures`);
      setSelectionProcedures(response.data || []);
    } catch (error) {
      console.error('Error loading selection procedures:', error);
      toast.error('Failed to load selection procedures');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (procedure = null) => {
    setEditingProcedure(procedure);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      id: (formData.get('id') || '').toUpperCase(),
      description: formData.get('description')
    };

    try {
      if (editingProcedure) {
        await axios.put(`${BACKEND_URL}/api/selection-procedures/${editingProcedure.id}`, {
          description: data.description
        });
        toast.success('Selection Procedure updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/selection-procedures`, data);
        toast.success('Selection Procedure created successfully');
      }
      setIsDialogOpen(false);
      loadSelectionProcedures();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save selection procedure');
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Selection Procedures</h1>
            <p className="text-sm text-gray-500 mt-1">Manage selection procedure configurations</p>
          </div>
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Selection Procedures</h2>
            {writable && (<Button onClick={() => handleOpenDialog()} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Selection Procedure
            </Button>)}
          </div>

          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : selectionProcedures.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No selection procedures found. Create one to get started.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {selectionProcedures.map((procedure) => (
                    <tr key={procedure.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{procedure.id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{procedure.description}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <RecordActions doctype="selection_procedures" onView={() => setViewItem(procedure)} onEdit={() => handleOpenDialog(procedure)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingProcedure ? 'Edit Selection Procedure' : 'Create Selection Procedure'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="id">ID * (1-2 characters)</Label>
                <Input
                  id="id"
                  name="id"
                  defaultValue={editingProcedure?.id || ''}
                  disabled={editingProcedure !== null}
                  required
                  maxLength={2}
                  minLength={1}
                  pattern="[A-Za-z0-9]{1,2}"
                  title="Must be 1-2 alphanumeric characters"
                  className="mt-1 uppercase"
                  placeholder="e.g., 1, 2, A"
                  onInput={(e) => {
                    e.target.value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 2);
                  }}
                />
                <p className="text-xs text-gray-500 mt-1">1-2 alphanumeric characters</p>
              </div>
              <div>
                <Label htmlFor="description">Description *</Label>
                <Input
                  id="description"
                  name="description"
                  defaultValue={editingProcedure?.description || ''}
                  required
                  maxLength={100}
                  className="mt-1"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingProcedure ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default SelectionProcedure;
